/**
 * Format XML string with proper indentation
 * @param xml - XML string to format
 * @param nestedXmlTags - Optional tag name(s) containing nested XML to format recursively (e.g., ['VDIXML'])
 * @param indent - Indentation string (default: 4 spaces)
 * @param options - { reEncodeNested?: boolean } whether to re-encode nested XML back to entities (default: true)
 * @returns Formatted XML string
 */
export const formatXML = (
  xml: string,
  nestedXmlTags?: string[] | string,
  indent: string = '    ',
  options?: { reEncodeNested?: boolean },
): string => {
  if (!xml || typeof xml !== 'string') {
    return xml;
  }

  try {
    const reEncodeNested = options?.reEncodeNested ?? true;
    if (nestedXmlTags) {
      const tags = Array.isArray(nestedXmlTags)
        ? nestedXmlTags
        : [nestedXmlTags];
      tags.forEach((nestedXmlTag) => {
        const tagPattern = new RegExp(
          `(<${nestedXmlTag}(?:\\s[^>]*)?>)([\\s\\S]*?)(<\\/${nestedXmlTag}>)`,
          'gi',
        );
        xml = xml.replace(tagPattern, (match, openTag, content, closeTag) => {
          const decodedContent = content
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&apos;/g, "'")
            .replace(/&amp;/g, '&');

          const formattedNestedXml = formatXMLInternal(
            decodedContent.trim(),
            indent,
          );

          const precedingText = xml.substring(0, xml.indexOf(match));
          const lastLineBreak = precedingText.lastIndexOf('\n');
          const lineBeforeTag =
            lastLineBreak >= 0
              ? precedingText.substring(lastLineBreak + 1)
              : precedingText;
          const baseIndent = lineBeforeTag.match(/^\s*/)?.[0] || '';
          const contentIndent = baseIndent + indent;

          const processedLines = formattedNestedXml
            .split('\n')
            .map((line) => {
              if (!line.trim()) return '';
              const outLine = reEncodeNested
                ? line
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/\"/g, '&quot;')
                    .replace(/'/g, '&apos;')
                : line;
              return contentIndent + outLine;
            })
            .filter(Boolean)
            .join('\n');

          return `${openTag}\n${processedLines}\n${baseIndent}${closeTag}`;
        });
      });
    }

    return formatXMLInternal(xml, indent);
  } catch (error) {
    console.error('Error formatting XML:', error);
    return xml;
  }
};

const formatXMLInternal = (xml: string, indent: string = '    '): string => {
  let formatted = '';
  let indentLevel = 0;

  xml = xml.replace(/>\s*</g, '><');

  const nodes = xml.split(/(?=<)/);

  nodes.forEach((node) => {
    if (!node.trim()) return;

    const isClosingTag = /^<\//.test(node);
    const isSelfClosing = /\/>$/.test(node);
    const isOpeningTag = /^<[^\/?!]/.test(node) && !isSelfClosing;

    if (isClosingTag) {
      indentLevel--;
    }

    formatted += `${indent.repeat(Math.max(0, indentLevel)) + node.trim()}\n`;

    if (isOpeningTag) {
      indentLevel++;
    }
  });

  return formatted.trim();
};
